// src/services/base.api.js
import i18n from '../utils/i18next';
import { offlineApiService } from '../offline/services/offline-api.service.js';
import { mutationQueue } from '../offline/queue/mutation-queue.js';
import { offlineStateService } from '../offline/services/offline-state.service.js';
import { cacheDebugLog } from '../offline/utils/cache-debug.js';
import { inferQueuedMutationMeta } from '../offline/utils/sync-queue-meta.js';
import { fetchWithTimeout, FETCH_TIMEOUT_MS } from '../utils/fetch-with-timeout.js';

const MUTATION_FETCH_TIMEOUT_MS = 5000;

function shouldQueueMutationsNow() {
  offlineStateService.syncFromNavigator();
  const navigatorOffline =
    typeof navigator !== 'undefined' && navigator.onLine === false;
  return offlineStateService.isCurrentlyOffline() || navigatorOffline;
}
import { authOfflineService } from './auth-offline.service.js';
import { removeToken, setToken } from './storage/localStorage.service.js';
import { API_CONFIG } from '../config/api.config.js';
import { refreshCoordinator } from '../offline/auth/refresh-coordinator.js';

const BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
const DISABLE_OFFLINE_GET_CACHE = import.meta.env.VITE_DISABLE_OFFLINE_API_CACHE === 'true';

// Helper function to get translated error message
const getErrorMessage = (status, message) => {
  if (!message) {
    switch (status) {
      case 0:
        return i18n.t('errors.networkError');
      case 500:
        return i18n.t('errors.serverError');
      case 503:
        return i18n.t('errors.serviceUnavailable');
      case 504:
        return i18n.t('errors.timeoutError');
      default:
        return i18n.t('errors.unknownError');
    }
  }

  // Check for specific error patterns
  if (
    message.includes('MongoDB') ||
    message.includes('database') ||
    message.includes('ECONNREFUSED') ||
    message.includes('Database unavailable')
  ) {
    return i18n.t('errors.databaseError');
  }
  if (message.includes('timeout') || message.includes('ETIMEDOUT')) {
    return i18n.t('errors.timeoutError');
  }
  if (
    message.includes('Network Error') ||
    message.includes('Failed to fetch') ||
    message.includes('ENOTFOUND')
  ) {
    return i18n.t('errors.networkError');
  }

  return message;
};

// ── Instrumentation counters for race-condition diagnostics ──
let _refreshRequestId = 0;
const _refreshLog = (event, extra) => {
  const ts = Date.now();
  const at = localStorage.getItem('accessToken');
  const tokenPrefix = at ? at.substring(0, 10) : 'null';
  console.log(
    `[BASEAPI-AUTH] ${ts} rid:${_refreshRequestId} ${event}` +
    ` token:${tokenPrefix}` +
    (extra ? ` ${extra}` : '')
  );
};

let isRefreshing = false;

let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

function isNetworkIssue(error) {
  const message = String(error?.message || error?.name || '');
  return [
    'Failed to fetch',
    'NetworkError',
    'Network request failed',
    'TypeError',
    'timeout',
    'AbortError',
    'Offline',
  ].some((fragment) => message.includes(fragment));
}

async function refreshToken() {
  _refreshRequestId++;
  const rid = _refreshRequestId;
  _refreshLog('REFRESH_STARTED', `rid:${rid}`);

  try {
    const accessToken = await refreshCoordinator.executeRefresh(`${BASE_URL}/auth/refresh`);
    const newTokenPrefix = accessToken ? accessToken.substring(0, 10) : 'null';
    _refreshLog('REFRESH_SUCCEEDED', `rid:${rid} newTokenPrefix:${newTokenPrefix}`);

    setToken(accessToken);
    _refreshLog('TOKEN_AFTER_SET', `rid:${rid} token:${localStorage.getItem('accessToken')?.substring(0,10) || 'null'}`);

    return accessToken;
  } catch (error) {
    if (error.status === 401 || error.status === 403) {
      if (!authOfflineService.isOfflineGraceValid()) {
        _refreshLog('REMOVE_TOKEN_EXECUTED', `rid:${rid} status:${error.status}`);
        removeToken(/* force = */ true);
        _refreshLog('TOKEN_AFTER_REMOVE', `rid:${rid} token:${localStorage.getItem('accessToken')?.substring(0,10) || 'null'}`);
      }
    }

    if (isNetworkIssue(error)) {
      error.offlineRefresh = true;
      _refreshLog('REFRESH_CATCH_NETWORK', `rid:${rid} msg:${error.message}`);
      throw error;
    }

    _refreshLog('REFRESH_CATCH_ERROR', `rid:${rid} msg:${error.message}`);
    throw error;
  }
}

async function request(url, options = {}, isRetry = false) {
  const token = localStorage.getItem('accessToken');

  const headers = {
    'Content-Type': 'application/json',

    ...(token && { Authorization: `Bearer ${token}` }),

    ...options.headers,
  };

  const fullUrl = `${BASE_URL}${url}`;

  const isGet = options.method === 'GET' || !options.method;

  offlineStateService.syncFromNavigator();

  // Block offline usage if the offline grace period has expired.
  if (offlineStateService.isCurrentlyOffline() && !authOfflineService.isOfflineUsageAllowed()) {
    throw new Error(
      i18n.t('offline.graceExpired')
    );
  }

  // For GET requests, use offline API service for caching (unless isolation toggle)
  if (isGet && !DISABLE_OFFLINE_GET_CACHE) {
    try {
      cacheDebugLog('baseapi-get-offline-layer', { fullUrl });
      const data = await offlineApiService.get(fullUrl, {
        headers,
        credentials: 'include',
      });
      offlineStateService.markNetworkSuccess();
      return data;
    } catch (offlineError) {
      const isOfflineError =
        offlineStateService.isCurrentlyOffline() ||
        String(offlineError?.message || '').includes('Offline');

      cacheDebugLog('baseapi-get-offline-layer-failed', {
        fullUrl,
        error: offlineError.message,
        isOfflineError,
      });

      // Do not fall through to a hanging fetch when offline or cache is empty
      if (isOfflineError) {
        throw offlineError;
      }

      console.log('[BaseAPI] Offline API failed, trying direct fetch:', offlineError.message);
      cacheDebugLog('baseapi-get-fallback-direct', { fullUrl, error: offlineError.message });
    }
  } else if (isGet && DISABLE_OFFLINE_GET_CACHE) {
    cacheDebugLog('baseapi-get-direct-only', { fullUrl });
  }

  const payloadParsed =
    options.body && (options.method === 'POST' || options.method === 'PUT')
      ? (() => {
          try {
            return JSON.parse(options.body);
          } catch {
            return {};
          }
        })()
      : {};

  // For POST/PUT/DELETE requests, handle offline by queuing mutations.
  const queueMutation = async () => {
    console.log('[BaseAPI] Queuing mutation due to offline or network error:', options.method, url);

    const { entityType, entityId } = inferQueuedMutationMeta(
      url,
      options.method,
      payloadParsed
    );

    const operationMap = {
      'POST': 'create',
      'PUT': 'update',
      'DELETE': 'delete',
    };
    const operationType = operationMap[options.method];

    try {
      const payloadForQueue = { ...payloadParsed };
      if (options.method === 'POST' && !payloadForQueue.clientRequestId) {
        payloadForQueue.clientRequestId = `cr-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
      }

      // ── Auto-detect dependencies ──────────────────────────────────────
      // If this mutation references a temp ID (cr-* / temp-*) from a pending
      // CREATE mutation, add that CREATE as a dependency so we never attempt
      // this mutation before the parent entity exists on the server.
      const dependencies = [];
      try {
        const pending = await mutationQueue.getPending();
        const pendingCreates = pending.filter(
          m => m.operationType === 'create' && m.entityId &&
               (String(m.entityId).startsWith('cr-') || String(m.entityId).startsWith('temp-'))
        );
        if (pendingCreates.length > 0) {
          const payloadStr = JSON.stringify(payloadForQueue);
          for (const create of pendingCreates) {
            const tempId = String(create.entityId);
            const matchesPayload = payloadStr && payloadStr.includes(tempId);
            const matchesUrl     = fullUrl && fullUrl.includes(tempId);
            const matchesOptId   = create.payload?._optimisticId && (
              payloadStr?.includes(create.payload._optimisticId) ||
              fullUrl?.includes(create.payload._optimisticId)
            );
            if (matchesPayload || matchesUrl || matchesOptId) {
              dependencies.push(create.mutationId);
            }
          }

          // ── Safety net: temp-* heuristic ────────────────────────────────
          // If the new mutation's payload contains ANY value matching temp-*
          // (e.g. athleteId: "temp-xxx"), auto-establish a dependency on ALL
          // pending creates with a matching _optimisticId.  This catches cases
          // where the create hook sets _optimisticId but the dependency scan
          // above missed it due to nested payload paths.
          const tempValuesInPayload = Object.values(payloadForQueue)
            .filter(v => typeof v === 'string' && v.startsWith('temp-'));
          if (tempValuesInPayload.length > 0) {
            for (const create of pendingCreates) {
              const optId = create.payload?._optimisticId;
              if (optId && tempValuesInPayload.includes(optId)) {
                if (!dependencies.includes(create.mutationId)) {
                  dependencies.push(create.mutationId);
                }
              }
            }
          }
        }
      } catch (scanErr) {
        // Non-blocking: dependency scan failure should never break enqueue
        console.warn('[BaseAPI] Dependency scan failed (non-blocking):', scanErr);
      }

      cacheDebugLog('baseapi-enqueue-start', { method: options.method, url, dependencies });
      await mutationQueue.enqueue({
        entityType,
        entityId: payloadForQueue.clientRequestId || entityId,
        operationType,
        payload: payloadForQueue,
        url: fullUrl,
        method: options.method,
        headers,
        dependencies,
      });
      cacheDebugLog('baseapi-enqueue-done', { method: options.method, url });
      console.log('[BaseAPI] Mutation queued successfully');
      return {
        queued: true,
        message: 'Operation queued for sync when online',
        data: payloadParsed,
      };
    } catch (queueError) {
      console.error('[BaseAPI] Failed to queue mutation:', queueError);
      throw queueError;
    }
  };

  const isSyncEndpoint = url === '/sync' || url.startsWith('/sync?');
  const isAuthEndpoint = url.startsWith('/auth/');
  const isSearchEndpoint =
    options.method === 'POST' &&
    (url === API_CONFIG.ENDPOINTS.ATHLETE.SEARCH_BY_NAME ||
      url === API_CONFIG.ENDPOINTS.TRAINER.SEARCH_BY_NAME);

  // Read-only search POSTs should hit the offline cache layer, not mutation queue
  if (isSearchEndpoint) {
    return offlineApiService.post(fullUrl, payloadParsed, {
      headers,
      credentials: 'include',
    });
  }

  // Auth must always hit the network — never queue login/refresh/logout
  if (['POST', 'PUT', 'DELETE'].includes(options.method) && !isSyncEndpoint && !isAuthEndpoint) {
    const willQueue = shouldQueueMutationsNow();
    cacheDebugLog('baseapi-mutation-route', {
      method: options.method,
      url,
      navigatorOnLine: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
      serviceOnline: offlineStateService.isCurrentlyOnline(),
      willQueue,
    });

    if (willQueue) {
      if (!authOfflineService.isOfflineUsageAllowed()) {
        throw new Error(
          i18n.t('offline.graceExpired')
        );
      }
      return queueMutation();
    }
  }

  cacheDebugLog('baseapi-fetch', { method: options.method || 'GET', url: fullUrl });

  const isMutation = ['POST', 'PUT', 'DELETE'].includes(options.method);
  const fetchTimeout = isMutation ? MUTATION_FETCH_TIMEOUT_MS : FETCH_TIMEOUT_MS;

  let response;
  try {
    response = await fetchWithTimeout(
      fullUrl,
      {
        ...options,
        headers,
        credentials: 'include', // Include cookies with all requests
      },
      fetchTimeout
    );
  } catch (fetchError) {
    const networkIssue =
      ['Failed to fetch', 'NetworkError', 'Network request failed', 'TypeError', 'timeout', 'AbortError'].some(
        (fragment) =>
          String(fetchError.message || fetchError.name || '').includes(fragment)
      );

    if (networkIssue) {
      offlineStateService.markNetworkFailure();

      if (
        ['POST', 'PUT', 'DELETE'].includes(options.method) &&
        !isAuthEndpoint
      ) {
        const error = new Error(i18n.t('errors.weakConnection', { defaultValue: 'Your Internet connection appears to be weak.' }));
        error.weakConnection = true;
        throw error;
      }
    }

    if (isGet && networkIssue) {
      throw fetchError;
    }

    throw fetchError;
  }

  // If 401 and not a retry, try to refresh token

  if (response.status === 401 && !isRetry) {
    if (isRefreshing) {
      // Wait for refresh to complete

      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })

        .then((token) => {
          // If a token was passed by processQueue, ensure it's set in localStorage
          if (token) {
            setToken(token);
          }
          return request(url, options, true);
        })

        .catch((err) => {
          throw err;
        });
    }

    isRefreshing = true;

    let newToken;
    try {
      newToken = await refreshToken();

      isRefreshing = false;

      processQueue(null, newToken);

      // Retry the original request

      return request(url, options, true);
    } catch (refreshError) {
      isRefreshing = false;

      processQueue(refreshError, null);

      throw refreshError;
    }
  }

  // 🔴 global error handling

    if (!response.ok) {
    if (response.status === 401 && !authOfflineService.isOfflineGraceValid()) {
      removeToken(/* force = */ true);
    }

    const errorData = await response.json().catch(() => ({}));

    // Translate common error messages
    if (errorData.message) {
      errorData.message = getErrorMessage(response.status, errorData.message);
    }

    throw { status: response.status, ...errorData };
  }

  // some DELETE requests return no body

  if (response.status === 204) return null;

  const json = await response.json();
  offlineStateService.markNetworkSuccess();
  if (isGet) {
    cacheDebugLog('baseapi-get-direct-response', { fullUrl, ok: response.ok });
  }
  return json;
}

export const baseApi = {
  get: (url) => request(url, { method: 'GET' }),

  post: (url, body) =>
    request(url, {
      method: 'POST',

      body: JSON.stringify(body),
    }),

  put: (url, body) =>
    request(url, {
      method: 'PUT',

      body: JSON.stringify(body),
    }),

  delete: (url) =>
    request(url, {
      method: 'DELETE',
    }),
};
